import { 
  Navigate, 
  Route, 
  useLocation, 
  useParams 
} from 'react-router-dom';

// Old links keep working after the platform area moved to `/admin` and the org/entity
// "Users" tab became "Access". The query string and hash always carry over.
type Target = (params: Record<string, string | undefined>, search: string) => string;

function Redirect({ to }: { to: Target }) {
  const params = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`${to(params, search)}${hash}`} replace />;
}

// `/platform/invitations` lands on a tab of the access page, so the section joins the query.
const withSection = (path: string, section: string): Target => (_p, search) => {
  const query = new URLSearchParams(search);
  query.set('section', section);
  return `${path}?${query.toString()}`;
};

const plain = (path: (p: Record<string, string | undefined>) => string): Target => (p, search) =>
  `${path(p)}${search}`;

// Settings used to be separate tabs; they are sections of one Settings page now.
const SETTING_SECTIONS: [string, string][] = [
  ['delegation', 'delegation'], ['sla', 'sla'], ['smtp', 'smtp'], ['roles', 'roles'], ['connectors', 'connectors']
];
const settingRedirects = (level: 'org' | 'entity') => SETTING_SECTIONS.flatMap(([tab, section]) => {
  // Entities never list delegation, so its old link lands on the Settings page itself.
  const sectionFor = level === 'entity' && section === 'delegation' ? null : section;
  const target: Target = (p, search) => {
    const base = `/${level}/${p.id}/settings`;
    if (!sectionFor) return `${base}${search}`;
    const to = withSection(base, sectionFor)(p, search);
    // A connector's detail page used to be `/connectors/<slug>`.
    const slug = section === 'connectors' ? p['*']?.split('/')[0] : undefined;
    return slug ? `${to}&connector=${slug}` : to;
  };
  return [`/${level}/:id/${tab}`, `/${level}/:id/${tab}/*`].map((path) => ({ 
    path, 
    to: target 
  }));
});

export const LEGACY_REDIRECTS: { path: string; to: Target }[] = [
  { 
    path: '/platform', 
    to: plain(() => '/admin') 
  }, { 
    path: '/platform/organisations', 
    to: plain(() => '/admin/organisations') 
  }, {
    path: '/platform/organisations/:organisationId',
    to: plain((p) => `/admin/organisations/${p.organisationId}`)
  }, { path: '/platform/users', to: plain(() => '/admin/access') }, { 
    path: '/platform/invitations', 
    to: withSection('/admin/access', 'invitations') 
  }, { 
    path: '/platform/audit', 
    to: plain(() => '/admin/audit') 
  }, { 
    path: '/platform/profile', 
    to: plain(() => '/profile') 
  }, { 
    path: '/org/:id/users', 
    to: plain((p) => `/org/${p.id}/access`) 
  }, { 
    path: '/org/:id/members', 
    to: plain((p) => `/org/${p.id}/access`) 
  }, { 
    path: '/entity/:id/users', 
    to: plain((p) => `/entity/${p.id}/access`) 
  }, { 
    path: '/entity/:id/members', 
    to: plain((p) => `/entity/${p.id}/access`) 
  }, { 
    path: '/settings/members', 
    to: plain(() => '/access') 
  }, { 
    path: '/settings/org-invitations', 
    to: plain(() => '/access') 
  }, ...settingRedirects('org'), ...settingRedirects('entity')
];

export const legacyRedirectRoutes = () =>
  LEGACY_REDIRECTS.map(({ path, to }) => (
    <Route key={path} path={path} element={<Redirect to={to} />} />
  ));
